import { inject, observer } from 'mobx-react';
import { action, observable } from 'mobx';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import styled from 'styled-components';
import { windowResize$ } from '../../util/dom/window-resize.util';
import { ellipsisText } from '../../util/style/ellipsis-text.util';
import { limitValueToRange } from '../../util/number/limit-value-to-range.util';
import { mapToRange } from '../../util/number/map-to-range.util';
import { addEventListenerToObject } from '../../util/dom/add-event-listener-to-object.util';
import { IInjectedProps } from '../../interface/component/injected-props.interface';
import { BlockColorEnum } from '../../enum/block-color.enum';
import { ColorEnum } from '../../enum/color.enum';
import { lightenColor } from '../../util/style/lighten-color.util';
import { getEventX } from '../../util/dom/get-event-x.util';
import {isNumberString} from '../../util/number/is-number-string.util';

const ELEMENT_HEIGHT = 44;

interface ITrack {
  disabled: boolean;
  isInvalid: boolean;
}

interface ITrackBall {
  left: number;
  selected: boolean;
}

const TRACKBALL_WIDTH = 35;
const TRACKBALL_HEIGHT = 35;
const TRACK_HEIGHT = 10;
const CONTAINER_PADDING_RIGHT = 5;
const CONTAINER_PADDING_LEFT = 5;
const CONTAINER_PADDING_TOP = 5;
const CONTAINER_PADDING_BOTTOM = 5;
const TRACK_MARGIN_LEFT = 20;
const TRACK_MARGIN_RIGHT = 20;
const BORDER_RADIUS = 12;
const SELECTED_BORDER_WIDTH = 2;

const TrackBall = styled.div`
  position: absolute;
  box-shadow: 0 2px 6px rgba(0,0,0,0.17);
  transform: translate3d(${(props: ITrackBall) => Math.round(props.left)}px, 0, 0);
  border: ${(props: ITrackBall) => props.selected ? `${SELECTED_BORDER_WIDTH}px solid ${lightenColor(BlockColorEnum.PrimaryButtonColor(props), -.1)}` : `${SELECTED_BORDER_WIDTH}px solid ${ColorEnum.Transparent(props)}`};
  background-color: ${BlockColorEnum.PrimaryButtonColor};
  width: ${TRACKBALL_WIDTH}px;
  height: ${TRACKBALL_HEIGHT}px;
  color: ${BlockColorEnum.SecondaryTextColor};
  text-align: center;
  top: -${Math.floor(TRACKBALL_HEIGHT / 2 - TRACK_HEIGHT / 2 + SELECTED_BORDER_WIDTH)}px;
  border-radius: ${BORDER_RADIUS}px;
  line-height: ${TRACKBALL_HEIGHT}px;
  z-index: 1;
  transition: background-color 0.25s;
`;

const TrackBallText = styled.div`
  max-width: 34px;
  padding-left: 5px;
  padding-right: 5px;
  ${ellipsisText};
`;
const Track = styled.div`
  background-color: ${(props: ITrack) => props.isInvalid ? BlockColorEnum.ErrorColor(props) : BlockColorEnum.InactiveColor(props)};
  box-shadow: inset 0px 1px 3px rgba(0,0,0,0.3);
  height:${TRACK_HEIGHT}px;
  width: calc(100% - ${TRACKBALL_WIDTH}px);
  border-radius: 5px;
  right: ${TRACK_MARGIN_RIGHT}px;
  left: ${TRACK_MARGIN_LEFT}px;
  top: calc(50% - ${Math.round(TRACK_HEIGHT / 2)}px);
  position: absolute;
  user-select: none;
  cursor: ${(props: ITrack) => props.disabled ? 'not-allowed' : 'ew-resize'};
`;
const ProgressBar = styled.div`
  background-color: ${(props) => lightenColor(BlockColorEnum.PrimaryButtonColor(props), -.1)};
  height:${TRACK_HEIGHT}px;
  width: ${(props: { width: number }) => props.width}px;
  border-radius: 5px;
  top: calc(50% - ${Math.round(TRACK_HEIGHT / 2)}px);
  left: 0;
  position: absolute;
  pointer-events: none;
`;
const Container = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: ${ELEMENT_HEIGHT}px;
  position:relative;
  padding-right: ${CONTAINER_PADDING_RIGHT - 1}px;
  padding-left: ${CONTAINER_PADDING_LEFT - 1}px;
  padding-top: ${CONTAINER_PADDING_TOP - 1}px;
  padding-bottom: ${CONTAINER_PADDING_BOTTOM - 1}px;
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
`;

const KeyboardListener = styled.input`
  width: 0px;
  height: 0px;
  position: absolute;
  z-index: -1;
  outline: none;
  &:focus {
    outline: none;
  }
  pointer-events: none;
`;

interface ISliderProps {
  value: number;
  name: string;
  disabled?: boolean;
  minValue: number;
  maxValue: number;
  onChange: (value: number) => void;
  onBlur?: () => void;
  isInvalid?: boolean;
  precision: number;
}

@inject('state')
@observer
export class SliderComponent extends React.Component<ISliderProps & IInjectedProps> {
  static defaultProps = {
    disabled: false,
    value: 0,
    minValue: -100,
    maxValue: 100,
    minLimitValue: 30,
    maxLimitValue: 60,
    trackColor: 'AltoGray',
    trackBallColor: 'GrayGreen',
    underMinLimitTrackBallColor: 'Blue',
    overMaxLimitTrackBallColor: 'Red',
    precision: 0
  };
  @observable dragging = false;
  @observable dragValue: number = 0;
  @observable selected: boolean = false;
  containerRect: ClientRect;
  track;
  resizeStream;
  onMouseUp;
  onTouchUp;
  onMouseMove;
  onTouchMove;
  keyboardListenerRef;
  keyboardInterval;

  focusKeyboardListener = () => {
    if (this.props.state.configuration.isMobile) {
      return;
    }
    ReactDOM.findDOMNode(this.keyboardListenerRef).focus();
  }

  @action
  handleDrag = e => {
    e.preventDefault();
    e.stopPropagation();
    if (this.dragging) {
      e.preventDefault();
      return;
    }
    const eventPageX = getEventX(e);
    const percentOfValue = limitValueToRange(((eventPageX - this.containerRect.left) / this.containerRect.width), 0, 1);
    this.setDragValue(mapToRange(percentOfValue, 0, 1, this.props.minValue, this.props.maxValue));
    this.dragging = true;

    this.onMouseUp = addEventListenerToObject(window, 'mouseup', this.handleEnd);
    this.onTouchUp = addEventListenerToObject(window, 'touchend', this.handleEnd);
    this.onMouseMove = addEventListenerToObject(window, 'mousemove', this.handleMouseMove);
    this.onTouchMove = addEventListenerToObject(window, 'touchmove', this.handleMouseMove);
    this.focusKeyboardListener();
  }
  removeListeners = () => {
    if (this.onMouseMove) {
      this.onMouseMove.remove();
      this.onMouseMove = undefined;
    }
    if (this.onMouseUp) {
      this.onMouseUp.remove();
      this.onMouseUp = undefined;
    }
    if (this.onTouchMove) {
      this.onTouchMove.remove();
      this.onTouchMove = undefined;
    }
    if (this.onTouchUp) {
      this.onTouchUp.remove();
      this.onTouchUp = undefined;
    }
  }

  @action
  handleEnd = e => {
    e.preventDefault();
    e.stopPropagation();
    if (!this.dragging) {
      e.preventDefault();
      return;
    }
    this.dragging = false;
    this.handleValueChange(event);
    this.removeListeners();
    this.focusKeyboardListener();
  }

  handleMouseMove = event => {
    event.preventDefault();
    event.stopPropagation();
    if (!this.dragging) {
      event.preventDefault();
      return;
    }
    const eventPageX = getEventX(event);
    const percentOfValue = limitValueToRange(((eventPageX - this.containerRect.left) / this.containerRect.width), 0, 1);
    this.setDragValue(mapToRange(percentOfValue, 0, 1, this.props.minValue, this.props.maxValue));
    this.focusKeyboardListener();
  }

  handleValueChange = (event) => {
    const {onChange} = this.props;
    const eventPageX = getEventX(event);
    const percentOfValue = limitValueToRange(((eventPageX - this.containerRect.left) / this.containerRect.width), 0, 1);
    const value = mapToRange(percentOfValue, 0, 1, this.props.minValue, this.props.maxValue);
    this.setDragValue(this.formatValueToPrecision(value));
    onChange(this.dragValue);
  }

  @action
  setDragValue = (value: number) => {
    this.dragValue = limitValueToRange(value, this.props.minValue, this.props.maxValue);
  }

  formatValueToPrecision = (value: number) => {
    if (this.props.precision === 0) {
      return Math.round(value);
    } else {
      return Number(value.toFixed(this.props.precision));
    }
  }

  componentDidMount() {
    this.resizeStream = windowResize$().subscribe(() => {
      if (this.track) {
        this.containerRect = ReactDOM.findDOMNode(this.track).getBoundingClientRect();
      }
      this.forceUpdate();
    });
  }

  componentWillUnmount() {
    this.removeListeners();
    this.cleaInterval();
    this.resizeStream.unsubscribe();
  }

  nudgeRight = () => {
    this.setDragValue(this.dragValue + 1);
    this.props.onChange(this.dragValue);
  }

  nudgeLeft = () => {
    this.setDragValue(this.dragValue - 1);
    this.props.onChange(this.dragValue);
  }

  onKeyDown = (event) => {
    if (this.keyboardInterval) {
      return;
    }
    const timeoutFunction = (nudgeFunction) => () => {
      nudgeFunction();
      console.log('nudged timeout');
      this.keyboardInterval = setTimeout(timeoutFunction(nudgeFunction), 150);
    };
    if (event.keyCode === 39) { // RightArrow
      this.keyboardInterval = setTimeout(timeoutFunction(this.nudgeRight), 150);
    } else if (event.keyCode === 37) { // LeftArrow
      this.keyboardInterval = setTimeout(timeoutFunction(this.nudgeLeft), 150);
    }
  }

  onKeyUp = (event) => {
    this.cleaInterval();
    if (event.keyCode === 39) { // RightArrow
      this.nudgeRight();
    } else if (event.keyCode === 37) { // LeftArrow
      this.nudgeLeft();
    }
  }

  cleaInterval = () => {
    if (this.keyboardInterval) {
      clearInterval(this.keyboardInterval);
      this.keyboardInterval = null;
    }
  }

  @action
  selectComponent = () => {
    this.selected = true;
  }

  @action
  unselectComponent = () => {
    this.selected = false;
    this.cleaInterval();
    if (this.props.onBlur) {
      this.props.onBlur();
    }
  }

  onChangeInput = (e) => {
    let newValue;
    if (isNumberString(e.target.value)) {
      newValue = Number(e.target.value);
    } else {
      newValue = this.props.value;
    }
    newValue = limitValueToRange(newValue, this.props.minValue, this.props.maxValue);
    this.setDragValue(this.formatValueToPrecision(newValue));
    this.props.onChange(this.dragValue);
  }

  render() {
    const {props} = this;
    const value = this.dragging ? this.dragValue : props.value;
    const progressWidth = this.containerRect ? mapToRange(value, props.minValue, props.maxValue, 0, 1) * this.containerRect.width : 0;

    return (
      <Container>
        <KeyboardListener
          id={this.props.name}
          name={this.props.name}
          ref={(kl) => this.keyboardListenerRef = kl}
          value={props.value || 0}
          onChange={this.onChangeInput}
          type="number"
          onKeyDown={this.onKeyDown}
          onKeyUp={this.onKeyUp}
          onFocus={this.selectComponent}
          onBlur={this.unselectComponent}
        />
        <Track
          ref={(track) => {
            if (track) {
              this.containerRect = ReactDOM.findDOMNode(track).getBoundingClientRect();
              this.track = track;
            }
          }}
          isInvalid={props.isInvalid}
          disabled={props.disabled}
          onMouseDown={props.disabled ? undefined : this.handleDrag}
          onTouchStart={props.disabled ? undefined : this.handleDrag}
        >
          <ProgressBar
            width={progressWidth}
          />
          <TrackBall
            selected={this.selected}
            left={progressWidth - (TRACKBALL_WIDTH / 2)}
            onMouseDown={props.disabled ? undefined : this.handleDrag}
            onTouchStart={props.disabled ? undefined : this.handleDrag}
          >
            <TrackBallText>
              {this.formatValueToPrecision(value)}
            </TrackBallText>
          </TrackBall>
        </Track>
      </Container>
    );
  }
}
